import { useEffect, useRef, useState, type ReactNode } from "react";
import type { WordDTO } from "@shared/types";
import { shuffle } from "@shared/game";
import { speak, speakPromptOnce, type LoopHandle } from "../../audio";
import { GameImage } from "../GameImage";
import { useT } from "../../../local/i18n";
import { viAudioUrl } from "../../../local/languageSwap";

export interface ChallengeProps {
  word: WordDTO;
  distractors: WordDTO[];
  onAnswer: (correct: boolean) => void;
}

interface ChoiceGridProps {
  choices: WordDTO[];
  correctId: string;
  picked: string | null;
  onPick: (c: WordDTO) => void;
  render: (c: WordDTO) => ReactNode;
  columns?: 2 | 3;
}

export function ChoiceGrid({
  choices,
  correctId,
  picked,
  onPick,
  render,
  columns = 2,
}: ChoiceGridProps) {
  return (
    <div
      className="grid"
      style={{
        gridTemplateColumns: `repeat(${columns}, 1fr)`,
        gap: 12,
        marginTop: 16,
      }}
    >
      {choices.map((c) => {
        const correct = c.id === correctId;
        const isPicked = picked === c.id;
        const cls = picked
          ? isPicked
            ? correct
              ? "good"
              : "bad"
            : correct
              ? "good faded"
              : "faded"
          : "";
        return (
          <button
            key={c.id}
            className={`choice ${cls}`}
            onClick={() => onPick(c)}
            disabled={!!picked}
          >
            {render(c)}
          </button>
        );
      })}
    </div>
  );
}

export function useChoices(word: WordDTO, distractors: WordDTO[], retryKey = 0) {
  // Stable per (wordId, distractor ids, retryKey). Bumping retryKey on a
  // wrong answer reshuffles the order so the student can't memorise
  // "tap the 3rd button" — they have to recognise the word again.
  const key = `${word.id}:${distractors.map((d) => d.id).join(",")}:${retryKey}`;
  const ref = useRef<{ key: string; value: WordDTO[] } | null>(null);
  if (!ref.current || ref.current.key !== key) {
    ref.current = { key, value: shuffle([word, ...distractors.slice(0, 3)]) };
  }
  return ref.current.value;
}

export function useAudioPrompt(
  text: string,
  opts: { audioUrl?: string | null; vietnamese?: string | null } = {},
) {
  // NOTE: the `vietnamese` option is intentionally ignored on the initial
  // prompt and on the Replay button. Per design: the student only ever
  // hears Vietnamese on the WRONG-answer re-teach — never as part of the
  // question itself. Keeping the option in the signature so the challenge
  // components can keep passing the field without a refactor.
  void opts.vietnamese;
  const ref = useRef<LoopHandle | null>(null);
  const audioUrl = opts.audioUrl ?? null;
  useEffect(() => {
    if (!text) return;
    ref.current = speakPromptOnce(text, { audioUrl });
    return () => ref.current?.cancel();
  }, [text, audioUrl]);
  return {
    replay: () => {
      if (text) speak(text, { audioUrl, lang: "en-US" });
    },
    cancel: () => ref.current?.cancel(),
  };
}

interface ShellProps {
  tag: string;
  prompt?: string;
  imageUrl?: string | null;
  /** Word text to render under the prompt image so the student sees the
   *  spelling alongside the picture. Used whenever a picture and its
   *  word belong together in the question — image+text → audio, etc. */
  imageLabel?: string | null;
  speakerText?: string;
  speakerAudioUrl?: string | null;
  /** Vietnamese translation of speakerText. When present, the speaker
   *  auto-plays English then Vietnamese on appear, and the Replay button
   *  plays both as well. */
  speakerVietnamese?: string | null;
  children: ReactNode;
}

export function ChallengeImage({
  src,
  alt = "",
  className = "challenge-image",
}: {
  src?: string | null;
  alt?: string;
  className?: string;
}) {
  return <GameImage className={className} src={src} alt={alt} />;
}

export function ChallengeShell({
  tag,
  prompt,
  imageUrl,
  imageLabel,
  speakerText,
  speakerAudioUrl,
  speakerVietnamese,
  children,
}: ShellProps) {
  const t = useT();
  const speaker = useAudioPrompt(speakerText ?? "", {
    audioUrl: speakerAudioUrl,
    vietnamese: speakerVietnamese ?? null,
  });
  const speakerRef = useRef(speaker);
  speakerRef.current = speaker;
  const cardRef = useRef<HTMLDivElement>(null);

  // PC keyboard: number keys 1–4 pick the matching answer button; 0 replays
  // the audio. (Touch/tablet users just tap; badges are hidden there via CSS.)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const card = cardRef.current;
      if (!card) return;
      if (e.key >= "1" && e.key <= "4") {
        const btns = Array.from(card.querySelectorAll<HTMLButtonElement>(".choice"));
        const btn = btns[Number(e.key) - 1];
        if (btn && !btn.disabled) { e.preventDefault(); btn.click(); }
      } else if (e.key === "0") {
        e.preventDefault();
        speakerRef.current.replay();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Number each answer button (1..N) so PC players know which key to press.
  useEffect(() => {
    const card = cardRef.current;
    if (!card) return;
    card.querySelectorAll<HTMLButtonElement>(".choice").forEach((b, i) => {
      if (i < 9) b.dataset.num = String(i + 1);
      else delete b.dataset.num;
    });
  });

  return (
    <div className="challenge-overlay">
      <div className="challenge-card" ref={cardRef}>
        <div className="challenge-boost-banner">{t("⚡ WORD BOOST")}</div>
        <div className={`challenge-header ${speakerText ? "has-action" : ""}`}>
          <span className="challenge-tag">{t(tag)}</span>
          {speakerText && (
            <button
              className="btn btn-blue btn-pill"
              onClick={speaker.replay}
            >
              {t("🔊 Replay")} <span className="key-hint" aria-hidden>0</span>
            </button>
          )}
        </div>
        {imageUrl && <ChallengeImage src={imageUrl} />}
        {imageUrl && imageLabel && (
          <div className="challenge-image-label">{imageLabel}</div>
        )}
        {prompt && <h2 className="challenge-prompt">{t(prompt)}</h2>}
        {children}
      </div>
    </div>
  );
}

/**
 * Handle a pick with feedback delay. On a wrong answer, opens a short teaching
 * moment and says the correct word once. On a correct answer, shows a
 * "Correct!" / "X in a row!" celebration banner that pops in over the
 * challenge for ~700 ms.
 */
// Module-level streak so it persists across consecutive challenge mounts
// (each gate creates a fresh ChallengeRouter, so component-level state
// would reset every question and the "in a row" prompt would never fire).
let _correctStreak = 0;
function nextCelebration(): { text: string; tone: "ok" | "fire" | "star" } {
  _correctStreak += 1;
  if (_correctStreak <= 1) return { text: "Correct!", tone: "ok" };
  if (_correctStreak === 2) return { text: "2 in a row!", tone: "fire" };
  if (_correctStreak === 3) return { text: "3 in a row! 🔥", tone: "fire" };
  if (_correctStreak === 4) return { text: "4 in a row! 🌟", tone: "star" };
  return { text: `${_correctStreak} in a row! 🌟`, tone: "star" };
}
function resetStreak() { _correctStreak = 0; }

export function usePickHandler(word: WordDTO, onAnswer: (correct: boolean) => void) {
  const [picked, setPicked] = useState<string | null>(null);
  const [teaching, setTeaching] = useState(false);
  const [celebrate, setCelebrate] = useState<{ text: string; tone: "ok" | "fire" | "star"; key: number } | null>(null);
  // Bumped on every wrong answer. Consumers pass this to useChoices so the
  // re-presented question has its answer buttons in a different order.
  const [retryKey, setRetryKey] = useState(0);
  const timers = useRef<number[]>([]);

  useEffect(() => {
    return () => {
      timers.current.forEach(window.clearTimeout);
      timers.current = [];
    };
  }, []);

  const later = (fn: () => void, ms: number) => {
    const timer = window.setTimeout(fn, ms);
    timers.current.push(timer);
  };

  const handle = (c: WordDTO) => {
    if (picked) return;
    setPicked(c.id);
    const correct = c.id === word.id;
    if (correct) {
      const beat = nextCelebration();
      setCelebrate({ ...beat, key: Date.now() });
      later(() => onAnswer(true), 800);
      return;
    }
    resetStreak();
    // Wrong answer → 2-second teach moment with the audio repeated twice
    // (~2 s apart), then the question is RE-PRESENTED with the answer
    // buttons reshuffled. The student must get it right before the gate
    // releases — no advancing on a wrong tap.
    later(() => setTeaching(true), 400);
    // Re-teach: English once, then Vietnamese once. The student learns the
    // meaning, not just the sound→image link.
    later(() => speak(word.english, { audioUrl: word.audioUrl, lang: "en-US" }),  700);
    if (word.vietnamese && word.vietnamese.trim()) {
      later(() => speak(word.vietnamese, { lang: "vi-VN", audioUrl: viAudioUrl(word.vietnamese) }), 2400);
    }
    later(() => {
      // End of teaching: clear picked + bump retryKey so the choices
      // reshuffle. The same gate stays mounted — same word, fresh attempt.
      setTeaching(false);
      setPicked(null);
      setRetryKey((k) => k + 1);
    }, 4400);
  };

  const teachOverlay = teaching ? <TeachOverlay word={word} /> : null;
  const celebrateOverlay = celebrate
    ? <CelebrateBanner key={celebrate.key} text={celebrate.text} tone={celebrate.tone} />
    : null;
  return { picked, handle, teachOverlay, celebrateOverlay, retryKey };
}

function CelebrateBanner({ text, tone }: { text: string; tone: "ok" | "fire" | "star" }) {
  return (
    <div className={`celebrate-banner celebrate-${tone}`} aria-live="polite">
      <span>{text}</span>
    </div>
  );
}

function TeachOverlay({ word }: { word: WordDTO }) {
  const t = useT();
  return (
    <div className="teach-overlay">
      <div className="teach-card">
        <p className="muted teach-label">{t("The answer was…")}</p>
        {word.imageUrl && <ChallengeImage className="teach-image" src={word.imageUrl} alt={word.english} />}
        <h2 className="teach-english">{word.english}</h2>
        <div className="teach-progress" />
      </div>
    </div>
  );
}
